
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { Box, Button } from '@mui/material';
import MobileStepper from '@mui/material/MobileStepper';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useMemo, useState, useEffect, useRef } from 'react';
import { DescriptionBlock, SlidingBlock, StartCourseSharedData } from './SharedStuff';
import { ImageWithSkeleton } from '../../shared/components/imageLoading/ImageWithSkeleton';
import axiosInstance from '../../axios.config';
import { useParams } from "react-router-dom";

export const StartCourseOnMobile = ({
    scrollableDivRef, coursesSteps, currentStep, stepsTitles, handlePrevious, imageUrls,
    videoUrl, setVideoWatched, lngsltd, handleNext, handleSubmission, htmlContent, bgImgUrl }) => {

    const maxSteps = useMemo(() => coursesSteps?.length, [coursesSteps])
    const { id: courseId } = useParams();
    const [hasQuiz, setHasQuiz] = useState(false);

    const languagecultureSelected = JSON.parse(localStorage.getItem('cultureSelected'));

    useEffect(() => {
    const checkQuiz = async () => {
      if (!courseId) return;

      try {
        const response = await axiosInstance.get(`/Quiz/ByCourse/${courseId}`);
        setHasQuiz(!!response?.data);
      } catch (error) {
        console.error("Error checking quiz:", error);
        setHasQuiz(false);
      }
    };

    checkQuiz();
  }, [courseId]); // ✅ only run when courseId changes
    
    if (languagecultureSelected) {
        let descriptionData;
        try {
            // Parse the JSON stored in the database
            descriptionData = JSON.parse(htmlContent);

            // Extract English and Norwegian descriptions
            const englishDescription = descriptionData["en"] || "";
            const norwegianDescription = descriptionData["no"] || "";

            if (languagecultureSelected === 'en-US') {
                htmlContent = englishDescription;
            }

            if (languagecultureSelected === 'nb-NO') {
                htmlContent = norwegianDescription;
            }
        }
        catch {

        }
    }

    return (
        <Box 
            ref={scrollableDivRef} 
            className="fullHW" 
            display="flex" 
            flexDirection="column" 
            sx={{ 
                bgcolor: '#fafafa',
                pb: { xs: 'calc(56px + 100px)', sm: 'calc(56px + 90px)' } // MobileStepper (56px) + Quiz button space (100px/90px)
            }}
        >

            <Box width="100%" flexGrow={1} display="flex" flexDirection="column">

                {/* Step Title - Modern Design */}
                {coursesSteps[currentStep] && (
                    <Paper
                        square
                        elevation={0}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 60,
                            background: 'linear-gradient(135deg, #212529 0%, #343a40 100%)',
                            borderBottom: '3px solid #dc3545',
                            position: 'sticky',
                            top: 0,
                            zIndex: 100,
                        }}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                color: 'white',
                                fontWeight: 600,
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                fontSize: '1rem'
                            }}
                        >
                            {stepsTitles[currentStep]}
                        </Typography>    
                    </Paper>
                )}
                {/* Display Content */}
                <CourseInnerContent
                    coursesSteps={coursesSteps}
                    currentStep={currentStep}
                    imageUrls={imageUrls}
                    lngsltd={lngsltd}
                    setVideoWatched={setVideoWatched}
                    videoUrl={videoUrl}
                />

                <DescriptionBlock
                    htmlContent={htmlContent}
                    bgImgUrl={bgImgUrl}
                />

            </Box>
            
            {/* Fixed Bottom Bar - Contains both Quiz Button and Navigation */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Submit Button - Above Navigation Bar */}
                {coursesSteps.length > 0 && currentStep >= coursesSteps.length - 1 && (
                    <Box 
                        sx={{
                            backgroundColor: '#ffffff',
                            borderTop: '1px solid #e5e7eb',
                            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
                            pt: 2.5,
                            px: 2,
                            pb: 1.5,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <Button
                            variant="contained"
                            sx={{ 
                                width: '100%',
                                maxWidth: '400px',
                                height: '52px',
                                background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                borderRadius: '12px',
                                fontWeight: 700,
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                textTransform: 'none',
                                boxShadow: '0 6px 20px rgba(220, 53, 69, 0.35)',
                                transition: 'all 0.3s ease',
                                letterSpacing: '0.02em',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: 1.3,
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #c82333 0%, #bd2130 100%)',
                                    boxShadow: '0 8px 24px rgba(220, 53, 69, 0.45)',
                                    transform: 'translateY(-2px)',
                                }
                            }}
                            endIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                            onClick={handleSubmission}
                        >
                            {hasQuiz
                                ? lngsltd["Let's Test Your Knowledge!"]
                                : lngsltd["Submit"]}
                        </Button>
                    </Box>
                )}
                
                {/* MobileStepper Navigation Bar - Always at the bottom */}
                <MobileStepper
                    variant="text"
                    steps={maxSteps}
                    position="bottom"
                    activeStep={currentStep}
                    sx={{ 
                        background: 'linear-gradient(135deg, #212529 0%, #343a40 100%)',
                        borderTop: coursesSteps.length > 0 && currentStep >= coursesSteps.length - 1 ? '1px solid #e5e7eb' : '2px solid #dc3545',
                        height: '56px',
                        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.15)',
                        position: 'relative',
                        zIndex: 1001
                    }}
                nextButton={
                    <Button
                        size="medium"
                        onClick={handleNext}
                        disabled={currentStep === maxSteps - 1}
                        sx={{ 
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            px: 2,
                            '&:disabled': {
                                color: 'rgba(255, 255, 255, 0.3)'
                            }
                        }}
                    >
                        {lngsltd["Next"]}
                        <KeyboardArrowRight />
                    </Button>
                }
                backButton={
                    <Button 
                        size="medium" 
                        onClick={handlePrevious} 
                        disabled={currentStep === 0}
                        sx={{
                            visibility: currentStep === 0 ? 'hidden' : 'visible',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            px: 2,
                            '&:disabled': {
                                color: 'rgba(255, 255, 255, 0.3)'
                            }
                        }}
                    >
                        <KeyboardArrowLeft />
                        {lngsltd["Back"]}
                    </Button>
                }
            />
            </Box>
        </Box>
    );
}

// Video component with aggressive sizing fix for mobile
const MobileVideoPlayer = ({ videoUrl, setVideoWatched, lngsltd }) => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [isMounted, setIsMounted] = useState(false);

    // Ensure component is mounted before accessing DOM
    useEffect(() => {
        // Small delay to ensure SlidingBlock transition has started
        const timer = setTimeout(() => {
            setIsMounted(true);
        }, 50);

        return () => clearTimeout(timer);
    }, []);

    // Aggressive sizing fix using useEffect
    useEffect(() => {
        if (!isMounted) return;

        const fixVideoSize = () => {
            // Add null checks and ensure elements are mounted
            if (!videoRef.current || !containerRef.current) {
                return;
            }

            try {
                const video = videoRef.current;
                const container = containerRef.current;
                
                // Check if container has valid dimensions and is in DOM
                if (!container.offsetWidth || !container.offsetHeight) {
                    return;
                }
                
                // Verify element is actually in the DOM (with try-catch for safety)
                let rect;
                try {
                    if (!container.getBoundingClientRect) {
                        return;
                    }
                    rect = container.getBoundingClientRect();
                    if (rect.width === 0 && rect.height === 0) {
                        return;
                    }
                } catch (e) {
                    // Element might not be in DOM yet
                    return;
                }
                
                // Get container dimensions safely
                const containerWidth = container.offsetWidth;
                const containerHeight = container.offsetHeight;
                
                // Only proceed if dimensions are valid
                if (containerWidth > 0 && containerHeight > 0 && video.style) {
                    video.style.width = `${containerWidth}px`;
                    video.style.height = `${containerHeight}px`;
                    video.style.minWidth = `${containerWidth}px`;
                    video.style.minHeight = `${containerHeight}px`;
                    video.style.maxWidth = `${containerWidth}px`;
                    video.style.maxHeight = `${containerHeight}px`;
                }
            } catch (error) {
                // Silently handle errors during transitions
            }
        };

        // Use requestAnimationFrame to ensure DOM is ready
        const rafId = requestAnimationFrame(() => {
            // Small delay to ensure SlidingBlock transition is complete
            setTimeout(() => {
                fixVideoSize();
            }, 150);
        });

        // Fix size on multiple events
        const video = videoRef.current;
        if (video) {
            const events = ['loadstart', 'loadedmetadata', 'loadeddata', 'canplay', 'playing', 'resize'];
            events.forEach(event => {
                video.addEventListener(event, fixVideoSize);
            });

            // Also use interval as fallback for first 3 seconds
            const intervalId = setInterval(() => {
                if (videoRef.current && containerRef.current) {
                    try {
                        const container = containerRef.current;
                        if (container.getBoundingClientRect) {
                            fixVideoSize();
                        }
                    } catch (e) {
                        // Ignore errors
                    }
                }
            }, 100);
            setTimeout(() => clearInterval(intervalId), 3000);

            return () => {
                cancelAnimationFrame(rafId);
                events.forEach(event => {
                    video.removeEventListener(event, fixVideoSize);
                });
            };
        }

        return () => {
            cancelAnimationFrame(rafId);
        };
    }, [videoUrl, isMounted]);

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                height: '100%',
                minWidth: '100%',
                minHeight: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                boxSizing: 'border-box',
                // Ensure element has dimensions for Slide to measure
                minHeight: '200px',
            }}
        >
            <video
                ref={videoRef}
                src={videoUrl}
                controls
                autoPlay
                preload="auto"
                muted
                style={{ 
                    width: '100%',
                    height: '100%',
                    minWidth: '100%',
                    minHeight: '100%',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    backgroundColor: '#000',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                    margin: 0,
                    padding: 0,
                    boxSizing: 'border-box',
                    position: 'relative',
                    zIndex: 1
                }}
                onEnded={() => setVideoWatched(true)}
                onError={(e) => {
                    console.error('Video loading error:', e);
                    console.error('Video URL:', videoUrl);
                    const video = e.target;
                    console.error('Video error details:', {
                        error: video.error,
                        networkState: video.networkState,
                        readyState: video.readyState
                    });
                }}
                                    onLoadStart={(e) => {
                                        const video = e.target;
                                        if (containerRef.current && containerRef.current.offsetWidth > 0) {
                                            try {
                                                const containerWidth = containerRef.current.offsetWidth;
                                                const containerHeight = containerRef.current.offsetHeight;
                                                if (containerWidth > 0 && containerHeight > 0) {
                                                    video.style.width = `${containerWidth}px`;
                                                    video.style.height = `${containerHeight}px`;
                                                }
                                            } catch (err) {
                                                // Ignore errors during transitions
                                            }
                                        }
                                    }}
                                    onLoadedMetadata={(e) => {
                                        const video = e.target;
                                        if (containerRef.current && containerRef.current.offsetWidth > 0) {
                                            try {
                                                const containerWidth = containerRef.current.offsetWidth;
                                                const containerHeight = containerRef.current.offsetHeight;
                                                if (containerWidth > 0 && containerHeight > 0) {
                                                    video.style.width = `${containerWidth}px`;
                                                    video.style.height = `${containerHeight}px`;
                                                }
                                            } catch (err) {
                                                // Ignore errors during transitions
                                            }
                                        }
                                    }}
                                    onLoadedData={(e) => {
                                        const video = e.target;
                                        if (containerRef.current && containerRef.current.offsetWidth > 0) {
                                            try {
                                                const containerWidth = containerRef.current.offsetWidth;
                                                const containerHeight = containerRef.current.offsetHeight;
                                                if (containerWidth > 0 && containerHeight > 0) {
                                                    video.style.width = `${containerWidth}px`;
                                                    video.style.height = `${containerHeight}px`;
                                                }
                                            } catch (err) {
                                                // Ignore errors during transitions
                                            }
                                        }
                                    }}
                                    onCanPlay={(e) => {
                                        const video = e.target;
                                        if (containerRef.current && containerRef.current.offsetWidth > 0) {
                                            try {
                                                const containerWidth = containerRef.current.offsetWidth;
                                                const containerHeight = containerRef.current.offsetHeight;
                                                if (containerWidth > 0 && containerHeight > 0) {
                                                    video.style.width = `${containerWidth}px`;
                                                    video.style.height = `${containerHeight}px`;
                                                }
                                            } catch (err) {
                                                // Ignore errors during transitions
                                            }
                                        }
                                    }}
            >
                {lngsltd["Your browser does not support the video tag"]}
            </video>
        </Box>
    );
};

const CourseInnerContent = ({ coursesSteps, currentStep, imageUrls, videoUrl, setVideoWatched, lngsltd }) => {

    if (!coursesSteps[currentStep]) return <></>

    if (coursesSteps[currentStep].contentType === 'Image') {
        return (
            <SlidingBlock currentStep={currentStep}>
                <Box
                    display="flex" 
                    flexDirection="column"
                    gap={2}
                    p={2}
                    sx={{ background: '#fafafa' }}
                >
                    {imageUrls.length > 0 && (
                        <>
                            {imageUrls.map((imageUrl, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                        background: '#fff',
                                        border: '1px solid #e0e0e0',
                                    }}
                                >
                                    <ImageWithSkeleton
                                        height="auto"
                                        width='100%'
                                        altText="course"
                                        animationDuration=""
                                        imageUrl={imageUrl}
                                        slideTo={StartCourseSharedData.getSlideTo()}
                                        slidingKey={currentStep}
                                    />
                                </Box>
                            ))}
                        </>
                    )}
                </Box>
            </SlidingBlock>
        )
    } else if (coursesSteps[currentStep].contentType === 'Video') {
        return (
            <Box p={2} sx={{ background: '#fafafa', width: '100%', boxSizing: 'border-box' }}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        aspectRatio: '16/9',
                        backgroundColor: '#000',
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        border: '1px solid #212529',
                        boxSizing: 'border-box',
                    }}
                >
                    {videoUrl ? (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                position: 'relative',
                            }}
                        >
                            <SlidingBlock currentStep={currentStep}>
                                <MobileVideoPlayer 
                                    videoUrl={videoUrl}
                                    setVideoWatched={setVideoWatched}
                                    lngsltd={lngsltd}
                                />
                            </SlidingBlock>
                        </Box>
                    ) : (
                        <Box 
                            sx={{ 
                                color: '#fff',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)'
                            }}
                        >
                            <b>{lngsltd["Loading...Please wait"]}</b>
                        </Box>
                    )}
                </Box>
            </Box>
        )
    }
}
